import {
  ActionIcon,
  Badge,
  Box,
  Button,
  Flex,
  Group,
  MantineTransition,
  NumberFormatter,
  Pill,
  Select,
  SelectProps,
  SimpleGrid,
  Text,
  TextInput,
  Title,
  Tooltip,
  Transition,
} from "@mantine/core";
import {
  IconAdjustmentsHorizontal,
  IconCalendarEvent,
  IconCheck,
  IconChevronDown,
  IconDownload,
  IconEye,
  IconFilterOff,
  IconReload,
  IconSearch,
} from "@tabler/icons-react";
import {
  MantineReactTable,
  MRT_ColumnDef,
  MRT_ColumnFiltersState,
  MRT_PaginationState,
  MRT_RowSelectionState,
  MRT_SortingState,
  useMantineReactTable,
} from "mantine-react-table";
import * as React from "react";
import {
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  useTransition,
} from "react";

import { DatePickerInput, DatesRangeValue } from "@mantine/dates";
import { useForm } from "@mantine/form";
import {
  useDisclosure,
  useHotkeys,
  useIsFirstRender,
  useReducedMotion,
} from "@mantine/hooks";
import { modals } from "@mantine/modals";
import moment from "moment";
import { useNavigate } from "react-router-dom";
import {
  keySearchSalesOrderListV2,
  repositoryPos,
  styleTableMantine,
} from "../../../_base/_const/_constVar";
import _breadcrumb from "../../../_base/component/_layout/_breadcrumb";
import { getValueById } from "../../../_base/helper/FunctionHelper";
import { SocketExtension } from "../../../_base/socket/socket";
import { CREATEORDERSUCCESS } from "../../../_base/socket/ToplicList";
import { formatDateTransfer } from "../../../common/FormatDate/FormatDate";
import LoadingOverlayCustom from "../../../common/LoadingOverLayCustom/LoadingOverLayCustom";
import { BASE_API_POS } from "../../../config";
import { _localization } from "../../../config/location";
import { green, olive_green, sky_blue } from "../../../const/variables";
import { MessageResponse } from "../../../model/MessageResponse";
import { SelectListItem } from "../../../model/SelectListItem";
import DetailsSellView from "../DetailsSellView";

// Màn chỉ xem của Danh sách đơn TMĐT (copy từ ListOrderTMDTV2):
// bỏ toàn bộ thao tác làm thay đổi đơn, chỉ giữ xem sản phẩm trong đơn,
// bộ lọc thu gọn thành thanh tìm kiếm nhanh + panel lọc mở rộng.

type AdvancedFilterField = {
  key: string;
  label: string;
  options?: { label: string; value: string }[];
  isDate?: boolean;
};

// Chiều cao bảng = màn hình - phần bộ lọc - 165px (thanh trên cùng của app,
// khoảng cách và thanh phân trang). Biến --tmdt-header-height được cập nhật
// thẳng vào DOM nên bảng không phải render lại khi phần bộ lọc đổi chiều cao.
const TABLE_HEIGHT =
  "max(300px, calc(100vh - var(--tmdt-header-height, 0px) - 165px))";

const ADVANCED_FILTERS_ID = "tmdt-read-only-advanced-filters";

// Panel bộ lọc chỉ animate opacity + transform, không animate chiều cao
// để trình duyệt không phải tính lại layout của bảng ở mỗi khung hình
const PANEL_TRANSITION: MantineTransition = {
  in: { opacity: 1, transform: "translateY(0)" },
  out: { opacity: 0, transform: "translateY(-6px)" },
  transitionProperty: "opacity, transform",
};

// Nhóm các ô lọc trong panel bộ lọc mở rộng
const FilterGroup = ({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}) => (
  <Box>
    <Text size="sm" fw={600} mb={6}>
      {title}
    </Text>
    <SimpleGrid
      cols={{ base: 1, xs: 2, md: 4 }}
      spacing="sm"
      verticalSpacing="xs"
    >
      {children}
    </SimpleGrid>
  </Box>
);

type FilterSectionProps = {
  quickFilters: React.ReactNode;
  chips: React.ReactNode;
  advancedFilters: React.ReactNode;
  activeCount: number;
  isLoading: boolean;
  onSearch: () => void;
  onClear: () => void;
};

// Tách riêng trạng thái đóng/mở panel để bấm "Bộ lọc" chỉ render lại phần này,
// không render lại cả bảng đơn hàng
const FilterSection = ({
  quickFilters,
  chips,
  advancedFilters,
  activeCount,
  isLoading,
  onSearch,
  onClear,
}: FilterSectionProps) => {
  const [opened, { toggle, close }] = useDisclosure(false);
  const reduceMotion = useReducedMotion();

  return (
    <>
      {/* Thanh tìm kiếm nhanh: luôn hiển thị */}
      <Flex gap="sm" align="center" wrap="wrap" mt="sm">
        {quickFilters}
        <Button
          variant={opened ? "light" : "default"}
          leftSection={<IconAdjustmentsHorizontal size={16} />}
          rightSection={
            <IconChevronDown
              size={14}
              style={{
                transform: opened ? "rotate(180deg)" : "none",
                transition: reduceMotion
                  ? undefined
                  : "transform 200ms cubic-bezier(0.16, 1, 0.3, 1)",
              }}
            />
          }
          aria-expanded={opened}
          aria-controls={ADVANCED_FILTERS_ID}
          onClick={toggle}
        >
          Bộ lọc
          {activeCount > 0 && (
            <Badge size="sm" circle ml={8}>
              {activeCount}
            </Badge>
          )}
        </Button>
        <Button
          leftSection={<IconSearch size={16} />}
          loading={isLoading}
          onClick={onSearch}
        >
          Tìm kiếm
        </Button>
      </Flex>

      {chips}

      <Transition
        mounted={opened}
        transition={PANEL_TRANSITION}
        duration={reduceMotion ? 0 : 180}
        exitDuration={reduceMotion ? 0 : 120}
        timingFunction="cubic-bezier(0.16, 1, 0.3, 1)"
        keepMounted
      >
        {(styles) => (
          <Box
            id={ADVANCED_FILTERS_ID}
            mt="sm"
            pt="sm"
            style={{
              ...styles,
              borderTop: "1px solid var(--mantine-color-gray-3)",
            }}
          >
            {advancedFilters}

            <Group justify="flex-end" gap="xs" mt="md">
              <Button
                variant="default"
                leftSection={<IconFilterOff size={16} />}
                onClick={onClear}
              >
                Xoá bộ lọc
              </Button>
              <Button
                leftSection={<IconCheck size={16} />}
                onClick={() => {
                  close();
                  onSearch();
                }}
              >
                Áp dụng
              </Button>
            </Group>
          </Box>
        )}
      </Transition>
    </>
  );
};

const ListOrderTMDTReadOnly = () => {
  const navigate = useNavigate();

  //#region  table

  //data and fetching state
  const [isHaveNewOrder, setIsHaveNewOrder] = useState(false);
  const [data, setData] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [rowCount, setRowCount] = useState(0);
  const [dataBranch, setDataBranch] = useState<SelectListItem[]>([]);
  const [listEmployeeSales, setListEmployeeSales] = useState<any[]>([]);
  const [visible, { close, open }] = useDisclosure(false);
  const [, startTransition] = useTransition();

  const dataChannel = [
    {
      text: "Lazada",
      value: "304",
    },
    {
      text: "Tiktok",
      value: "341",
    },
    {
      text: "Chat Zalo",
      value: "343",
    },
    {
      text: "Facebook",
      value: "62",
    },
    {
      text: "Shopee Mall",
      value: "63",
    },
    {
      text: "Shopee",
      value: "342",
    },
    {
      text: "Tổng đài",
      value: "66",
    },
    {
      text: "Khách đến showroom",
      value: "67",
    },
    {
      text: "Khách đặt đơn Website",
      value: "68",
    },
    {
      text: "Khách gọi điện cho nhân viên",
      value: "69",
    },
    {
      text: "Khách chat qua website",
      value: "101",
    },
  ];

  const dataStatusGeneral = [
    { label: "Kho xuất hàng", value: "KHO_XUAT_HANG" },
    { label: "Thành công", value: "THANH_CONG" },
    { label: "Test OK", value: "TEST_OK" },
    { label: "Đơn hàng hoàn thành", value: "DON_HANG_HOAN_THANH" },
    { label: "Đơn hàng tạm", value: "DON_HANG_TAM" },
    { label: "Thất bại do KH", value: "THAT_BAI_DO_KH" },
    { label: "KTKT đang kiểm tra", value: "KTKT_DANG_KIEM_TRA" },
    { label: "Đơn hàng chờ duyệt giảm", value: "DON_HANG_CHO_DUYET_GIAM" },
    { label: "Đơn chờ duyệt đổi trả", value: "DON_CHO_DUYET_DOI_TRA" },
    { label: "Đơn đã duyệt đổi trả", value: "DON_DA_DUYET_DOI_TRA" },
    { label: "GV thành công", value: "GV_THANH_CONG" },
    { label: "Nhập lại", value: "NHAP_LAI" },
    { label: "Đã phân công GV", value: "DA_PHAN_CONG_GV" },
    { label: "GV đến nơi", value: "GV_DEN_NOI" },
    { label: "GV đang giao", value: "GV_DANG_GIAO" },
    { label: "Test lỗi", value: "TEST_LOI" },
    { label: "Đã phân công KTKT", value: "DA_PHAN_CONG_KTKT" },
    { label: "Hủy phân công GV", value: "HUY_PHAN_CONG_GV" },
    { label: "Thất bại do HACOM", value: "THAT_BAI_DO_HACOM" },
    { label: "Nhập lại một phần", value: "NHAP_LAI_MOT_PHAN" },
    { label: "Đơn hàng chốt", value: "DON_HANG_CHOT" },
    { label: "Đơn hàng đã duyệt giảm", value: "DON_HANG_DA_DUYET_GIAM" },
    { label: "GV tiếp nhận", value: "GV_TIEP_NHAN" },
    { label: "GV nhập lại", value: "GV_NHAP_LAI" },
    { label: "GV nhập lại một phần", value: "GV_NHAP_LAI_MOT_PHAN" },
    { label: "GV thất bại do HACOM", value: "GV_THAT_BAI_DO_HACOM" },
    { label: "GV thất bại do KH", value: "GV_THAT_BAI_DO_KH" },
    { label: "GV nối chuyến", value: "GV_NOI_CHUYEN" },
    { label: "Hủy phân công KTKT", value: "HUY_PHAN_CONG_KTKT" },
    { label: "Có nhập lại", value: "CO_NHAP_LAI" },
    { label: "KH đã nhận", value: "DA_NHAN" },
  ];

  const checkedPinSearch = localStorage.getItem("checkedPinSearch") === "true";

  const _searchJson = sessionStorage.getItem(keySearchSalesOrderListV2)
    ? JSON.parse(sessionStorage.getItem(keySearchSalesOrderListV2) ?? "")
    : null;
  const firstRender = useIsFirstRender();
  //table state
  const [columnFilters, setColumnFilters] = useState<MRT_ColumnFiltersState>(
    [],
  );
  const [globalFilter, setGlobalFilter] = useState("");
  const [sorting, setSorting] = useState<MRT_SortingState>([]);
  const [pagination, setPagination] = useState<MRT_PaginationState>({
    pageIndex: 0,
    pageSize: 20,
  } as MRT_PaginationState);
  const [rowSelection] = useState<MRT_RowSelectionState>({});

  const innitStateSearch = useMemo(
    () => ({
      invId:
        firstRender && _searchJson && checkedPinSearch
          ? (_searchJson.InvId as string)
          : "",
      invOutwardId:
        firstRender && _searchJson && checkedPinSearch
          ? _searchJson.InvOutwardId
          : "",
      customerId: firstRender && _searchJson ? _searchJson.CustomerId : "",
      StartDate: (firstRender &&
      _searchJson &&
      _searchJson.StartDate &&
      checkedPinSearch
        ? new Date(_searchJson.StartDate)
        : new Date(
            new Date(
              new Date(new Date().setDate(new Date().getDate() - 9)).setHours(
                0,
                0,
                0,
                0,
              ),
            ).getTime() +
              7 * 60 * 60 * 1000,
          )) as Date | null,
      EndDate: (firstRender &&
      _searchJson &&
      checkedPinSearch &&
      _searchJson.EndDate &&
      _searchJson.EndDate != null
        ? new Date(_searchJson.EndDate)
        : new Date(
            new Date(
              new Date(new Date().setDate(new Date().getDate() + 1)).setHours(
                0,
                0,
                0,
                0,
              ),
            ).getTime() +
              7 * 60 * 60 * 1000,
          )) as Date | null,
      orderStatus:
        checkedPinSearch && firstRender && _searchJson
          ? _searchJson.OrderStatus
          : "",
      orderType:
        checkedPinSearch && firstRender && _searchJson
          ? _searchJson.OrderType
          : "",
      orderNumber:
        checkedPinSearch && firstRender && _searchJson
          ? _searchJson.OrderNumber
          : "",
      paymentStatus:
        checkedPinSearch && firstRender && _searchJson
          ? _searchJson.PaymentStatus
          : "",
      industry:
        checkedPinSearch && firstRender && _searchJson
          ? _searchJson.Industry
          : "",
      keySearch:
        checkedPinSearch && firstRender && _searchJson && checkedPinSearch
          ? _searchJson.KeySearch
          : "",
      OutwardStatus:
        checkedPinSearch && firstRender && _searchJson
          ? _searchJson.OutwardStatus
          : "",
      codeCustomer:
        checkedPinSearch && firstRender && _searchJson
          ? _searchJson.CodeCustomer
          : "",
      phone:
        checkedPinSearch && firstRender && _searchJson ? _searchJson.Phone : "",
      createBy:
        checkedPinSearch && firstRender && _searchJson
          ? _searchJson.CreateBy
          : "",
      //Người hưởng số
      CodeSal:
        firstRender && _searchJson && checkedPinSearch
          ? (_searchJson.CodeSal as string)
          : "",
      //Thời gian hoàn thành đơn
      FinishDate:
        firstRender && _searchJson && _searchJson.FinishDate && checkedPinSearch
          ? new Date(_searchJson.FinishDate)
          : null,
      //Ngày xuất kho
      OwDate:
        firstRender && _searchJson && _searchJson.OwDate && checkedPinSearch
          ? new Date(_searchJson.OwDate)
          : null,
      // Số điện thoại người bán
      SalePhone:
        checkedPinSearch && firstRender && _searchJson
          ? _searchJson.SalePhone
          : "",
      // Mã voucher
      VourcherCode:
        checkedPinSearch && firstRender && _searchJson
          ? _searchJson.VourcherCode
          : "",
      //Tình trạng phân công
      IsAssigned:
        checkedPinSearch && firstRender && _searchJson
          ? _searchJson.IsAssigned
          : "",
      //Đối soát
      AttributeValue7:
        checkedPinSearch && firstRender && _searchJson
          ? _searchJson.AttributeValue7
          : "",
      //Kênh bán
      saleChannelId:
        checkedPinSearch && firstRender && _searchJson
          ? _searchJson.saleChannelId
          : "",
      //Trạng thái đơn hàng TMĐT
      Attribute58:
        checkedPinSearch && firstRender && _searchJson
          ? _searchJson.Attribute58
          : "",
      //Trạng thái chung
      Attribute12:
        checkedPinSearch && firstRender && _searchJson
          ? _searchJson.Attribute12
          : "",
    }),
    [firstRender, _searchJson],
  );

  const form = useForm({
    mode: "uncontrolled",
    initialValues: innitStateSearch,
  });

  //#region bộ lọc
  // Giá trị bộ lọc đã áp dụng ở lần tìm kiếm gần nhất (dùng để hiển thị chip)
  const [appliedFilters, setAppliedFilters] =
    useState<Record<string, any>>(innitStateSearch);

  // Bảng co giãn theo chiều cao phần bộ lọc: thu gọn thì bảng cao hơn
  const rootRef = useRef<HTMLDivElement>(null);
  const headerRef = useRef<HTMLDivElement>(null);
  useLayoutEffect(() => {
    const root = rootRef.current;
    const header = headerRef.current;
    if (!root || !header) return;
    let lastHeight = -1;
    let frame = 0;
    const updateHeaderHeight = () => {
      const height = header.offsetHeight;
      if (height === lastHeight) return;
      lastHeight = height;
      root.style.setProperty("--tmdt-header-height", `${height}px`);
    };
    // Gán ngay trước lần vẽ đầu tiên để bảng không bị cao quá cỡ lúc mới vào
    updateHeaderHeight();
    // Dời việc cập nhật sang khung hình sau: đổi chiều cao bảng ngay trong callback
    // làm thanh cuộn của trang ẩn/hiện, phần bộ lọc đổi kích thước lần nữa trong
    // cùng khung hình và trình duyệt báo lỗi "ResizeObserver loop completed..."
    const observer = new ResizeObserver(() => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(updateHeaderHeight);
    });
    observer.observe(header);
    return () => {
      cancelAnimationFrame(frame);
      observer.disconnect();
    };
  }, []);
  //#endregion bộ lọc

  const getTextStatus = (row: any) => {
    return orderStatuses.find(
      (status) => status.value === row.original.orderStatus,
    )?.label;
  };

  //#region biến DL tĩnh
  const getShippingVendorText = (shippingVendor: string) => {
    switch (shippingVendor) {
      case "KHONG":
        return "Nhận tại cửa hàng";
      case "HACOM":
        return "Nội bộ";
      default:
        return shippingVendor;
    }
  };

  const TMDT_STATUS_MAP: Record<string | number, string> = {
    0: "Mới",
    17: "Chờ xác nhận",
    11: "Chờ hàng",
    12: "Chờ in",
    13: "Đã in",
    20: "Đã đặt hàng",
    1: "Đã xác nhận",
    8: "Đang đóng hàng",
    9: "Chờ chuyển hàng",
    2: "Đã gửi hàng",
    3: "Đã nhận",
    16: "Đã thu tiền",
    4: "Đang hoàn",
    15: "Hoàn một phần",
    5: "Đã hoàn",
    6: "Đã huỷ",
    7: "Đã xoá",
    CHO_LAY_HANG: "Chờ lấy hàng",
  };

  const getDataAtt58 = (value: string | number) => {
    if (value === null || value === undefined) return "";
    return TMDT_STATUS_MAP[value] || value;
  };

  const getDataDeliveryStatus = (value: string | null) => {
    switch (value) {
      case null:
        return "Không yêu cầu";
      case "THANH_CONG":
        return "Thành công";
      case "PHAN_CONG":
        return "Phân công";
      case "TAO_MOI":
        return "Tạo mới";
      case "THAT_BAI_DO_KH":
        return "Thất bại do khách hàng";
      case "NHAP_LAI":
        return "Nhập lại";
      case "DEN_NOI":
        return "Đến nơi";
      case "DANG_GIAO":
        return "Đang giao";
      case "DA_YEU_CAU":
        return "Đã yêu cầu";
      case "HUY_PHAN_CONG":
        return "Hủy phân công";
      case "THAT_BAI_DO_HACOM":
        return "Thất bại do HACOM";
      case "TIEP_NHAN":
        return "Tiếp nhận";
      case "NHAP_LAI_MOT_PHAN":
        return "Nhập lại một phần";
      case "NOI_CHUYEN":
        return "Nối chuyến";
    }
  };
  const orderTypes = [
    { value: "1", label: "Bán buôn", color: "yellow" },
    { value: "2", label: "Bán lẻ", color: "indigo" },
    { value: "3", label: "Đơn đổi trả", color: "green" },
    { value: "4", label: "Đơn online", color: "blue" },
  ];

  const orderStatuses = [
    { value: "DON_HANG_TAM", label: "Đơn hàng tạm" },
    { value: "DON_HANG_CHOT", label: "Đơn hàng chốt" },
    { value: "DON_HANG_CHO_DUYET_GIAM", label: "Đơn chờ duyệt giảm" },
    { value: "DON_HANG_DA_DUYET_GIAM", label: "Đơn đã duyệt giảm" },
    { value: "DON_HANG_HUY", label: "Đơn hàng hủy" },
    { value: "DON_HANG_HOAN_THANH", label: "Đơn hàng hoàn thành" },
    { value: "DON_HANG_DONG", label: "Đơn hàng đóng" },
  ];

  const warehouseStatus = [
    { value: "DA_YEU_CAU", label: "Đã yêu cầu" },
    { value: "DANG_XUAT", label: "Đang xuất" },
    { value: "DA_XUAT", label: "Đã xuất" },
    { value: "DANG_NHAP", label: "Đang nhập" },
    { value: "DA_NHAP", label: "Đã nhập" },
  ];

  const saleChannelOptions = dataChannel.map((channel) => ({
    label: channel.text,
    value: channel.value,
  }));

  const tmdtStatusOptions = [
    { label: "Mới", value: "0" },
    { label: "Chờ xác nhận", value: "17" },
    { label: "Chờ lấy hàng", value: "CHO_LAY_HANG" },
    { label: "Chờ hàng", value: "11" },
    { label: "Chờ in", value: "12" },
    { label: "Đã in", value: "13" },
    { label: "Đã đặt hàng", value: "20" },
    { label: "Đã xác nhận", value: "1" },
    { label: "Đang đóng hàng", value: "8" },
    { label: "Chờ chuyển hàng", value: "9" },
    { label: "Đã gửi hàng", value: "2" },
    { label: "Đã nhận", value: "3" },
    { label: "Đã thu tiền", value: "16" },
    { label: "Đang hoàn", value: "4" },
    { label: "Hoàn một phần", value: "15" },
    { label: "Đã hoàn", value: "5" },
    { label: "Đã huỷ", value: "6" },
    { label: "Đã xoá", value: "7" },
  ];

  const reconcileOptions = [
    { label: "Đã đối soát", value: "true" },
    { label: "Chưa đối soát", value: "false" },
  ];

  const assignOptions = [
    { label: "Đơn đã được phân công", value: "true" },
    { label: "Đơn chưa được phân công", value: "false" },
  ];

  const branchOptions = dataBranch.map((item) => ({
    label: item.text,
    value: item.value,
  }));
  // #endregion biến DL tĩnh

  //#region bộ lọc mở rộng
  const advancedFilterFields: AdvancedFilterField[] = [
    { key: "orderNumber", label: "Mã đơn hàng" },
    { key: "VourcherCode", label: "Mã voucher" },
    { key: "orderType", label: "Loại đơn hàng", options: orderTypes },
    { key: "saleChannelId", label: "Kênh bán", options: saleChannelOptions },
    {
      key: "orderStatus",
      label: "Trạng thái đơn hàng",
      options: orderStatuses,
    },
    {
      key: "Attribute58",
      label: "Trạng thái đơn TMĐT",
      options: tmdtStatusOptions,
    },
    {
      key: "Attribute12",
      label: "Trạng thái chung",
      options: dataStatusGeneral,
    },
    {
      key: "AttributeValue7",
      label: "Trạng thái đối soát",
      options: reconcileOptions,
    },
    { key: "codeCustomer", label: "Mã khách hàng" },
    { key: "phone", label: "SĐT khách hàng" },
    { key: "SalePhone", label: "SĐT người bán" },
    { key: "CodeSal", label: "Người hưởng số", options: listEmployeeSales },
    { key: "invId", label: "Chi nhánh", options: branchOptions },
    {
      key: "IsAssigned",
      label: "Tình trạng phân công",
      options: assignOptions,
    },
    { key: "FinishDate", label: "Ngày hoàn thành đơn", isDate: true },
    { key: "OwDate", label: "Ngày xuất kho", isDate: true },
  ];

  const isEmptyFilterValue = (value: any) =>
    value === undefined || value === null || value === "";

  const getFilterText = (field: AdvancedFilterField, value: any) => {
    if (field.isDate) return moment(value).format("DD/MM/YYYY");
    const option = field.options?.find(
      (item) => String(item.value) === String(value),
    );
    return option?.label ?? String(value);
  };

  const activeFilters = advancedFilterFields.filter(
    (field) => !isEmptyFilterValue(appliedFilters[field.key]),
  );
  //#endregion bộ lọc mở rộng

  // Xem chi tiết đơn hàng
  const handleCheckView = async (row: any) => {
    const url = `/api/v1/CreateSellItem/details?orderNumber=${row.original.orderNumber}`;
    const response = await repositoryPos.get<MessageResponse<any>>(url);
    if (response && response.success) {
      navigate("/sell/retail-order-tmdt", {
        state: {
          id: row.original.id,
          orderNumber: row.original.orderNumber,
          orderType: row.original.orderType,
          actionType: "view",
          fromTMDTList: true, // Thêm flag để nhận biết từ màn hình TMDT
        },
      });
    }
  };

  // Fetch lấy dữ liệu đơn hàng
  const fetchData = async () => {
    setIsLoading(true);

    let queryParams = {
      Skip:
        firstRender && _searchJson
          ? _searchJson.Skip
          : pagination.pageIndex * pagination.pageSize,
      Take: firstRender && _searchJson ? _searchJson.Take : pagination.pageSize,
    } as any;
    try {
      const search = form.getValues();
      setAppliedFilters(search);
      queryParams = {
        ...queryParams,
        InvId: search.invId?.toString(),
        InvOutwardId: search.invOutwardId?.toString(),
        CustomerId: search.customerId?.toString(),
        StartDate: search.StartDate
          ? moment(search?.StartDate).format("YYYY-MM-DDT00:00:00.000[Z]")
          : "",
        EndDate: search.EndDate
          ? moment(search?.EndDate).format("YYYY-MM-DDT00:00:00.000[Z]")
          : "",
        OrderStatus: search.orderStatus?.toString(),
        OrderType: search.orderType ? search.orderType?.toString() : "",
        OrderNumber: search.orderNumber?.toString(),
        PaymentStatus: search.paymentStatus?.toString(),
        industry: search.industry?.toString(),
        OutwardStatus: search?.OutwardStatus?.toString(),
        KeySearch: search.keySearch?.toString(),
        CodeCustomer: search.codeCustomer?.toString(), // Thêm trường tìm kiếm mã khách hàng
        Phone: search.phone?.toString(), // Thêm trường tìm kiếm số điện thoại
        CreateBy: search.createBy?.toString(), // Thêm trường tìm kiếm người tạo
        CodeSal: search.CodeSal?.toString(), //Thêm người hưởng số
        FinishDate: search.FinishDate
          ? moment(search?.FinishDate).format("YYYY-MM-DDT00:00:00.000[Z]")
          : "", //Thời gian hoàn thành đơn
        OwDate: search.OwDate
          ? moment(search?.OwDate).format("YYYY-MM-DDT00:00:00.000[Z]")
          : "", //ngày xuất kho
        SalePhone: search.SalePhone?.toString(), // Số điện thoại người bán
        VourcherCode: search.VourcherCode?.toString(), // Mã voucher
        saleChannelId: search.saleChannelId?.toString(),
        AttributeValue7: search.AttributeValue7?.toString(),
        Attribute58: search.Attribute58?.toString(), // Trạng thái đơn hàng TMĐT
        Attribute12: search.Attribute12?.toString(), // Trạng thái chung
      };

      const url =
        "/api/v1/CreateSellItem/get-list?IsOnline=false&IsInstallment=false&IsAccountant=false&IsEcommerce=true&" +
        Object.entries(queryParams)
          .filter(([, value]) => value !== undefined && value !== "")
          .map(
            ([key, value]) =>
              `${key}=${encodeURIComponent(
                value as string | number | boolean,
              )}`,
          )
          .join("&");

      const response = await repositoryPos.get<MessageResponse<any>>(url);

      if (response && response.success) {
        setData(response.data?.pagedList?.lists);
        startTransition(() => {
          setRowCount(response.totalCount);
        });
      }
    } catch (error) {
      console.error(error);
    } finally {
      setIsLoading(false);
      sessionStorage.setItem(
        keySearchSalesOrderListV2,
        JSON.stringify(queryParams),
      );
    }
  };

  // Tìm kiếm theo bộ lọc mới: luôn quay về trang đầu tiên
  const runSearch = () => {
    if (pagination.pageIndex !== 0) {
      setPagination((prev) => ({ ...prev, pageIndex: 0 }));
    } else {
      fetchData();
    }
  };

  const onEnterSearch = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      runSearch();
    }
  };

  const handleDateRangeChange = (value: DatesRangeValue) => {
    form.setFieldValue("StartDate", value[0], { forceUpdate: false });
    form.setFieldValue("EndDate", value[1], { forceUpdate: false });
  };

  const handleRemoveFilter = (field: AdvancedFilterField) => {
    form.setFieldValue(field.key as any, field.isDate ? null : "");
    runSearch();
  };

  const handleClearFilters = () => {
    form.setValues(
      Object.fromEntries(
        advancedFilterFields.map((field) => [
          field.key,
          field.isDate ? null : "",
        ]),
      ),
    );
    runSearch();
  };

  // Xem danh sách sản phẩm trong đơn hàng
  const openModal = (document: any, amount: number, order: string) =>
    modals.openConfirmModal({
      title: <Title order={4}>Danh sách sản phẩm trong đơn: {order}</Title>,
      children: <DetailsSellView e={document} amount={amount} />,
      size: "100%",
      radius: "md",
      labels: { confirm: "Confirm", cancel: "Cancel" },
      onCancel: () => console.log("Cancel"),
      onConfirm: () => console.log("Confirmed"),
      confirmProps: { display: "none" },
      cancelProps: { display: "none" },
    });

  const columns = React.useMemo<MRT_ColumnDef<any>[]>(
    () => [
      {
        header: "STT",
        accessorKey: "STT",
        Cell: ({ row }) => <>{row?.index + 1}</>,
        size: 80,
        enableSorting: false,
        enableResizing: false,
        enableColumnFilters: false,
      },
      {
        accessorKey: "orderNumber",
        header: "Mã đơn hàng",
        enableColumnFilter: false,
        size: 160,
        Cell: ({ renderedCellValue, row }) => (
          <Tooltip label="Xem chi tiết">
            <Badge
              radius="sm"
              variant="dot"
              size="md"
              style={{ cursor: "pointer" }}
              color={renderedCellValue === null ? "red" : green.base}
              onDoubleClick={() => handleCheckView(row)}
            >
              {renderedCellValue === null ? "KHÔNG HỢP LỆ" : renderedCellValue}
            </Badge>
          </Tooltip>
        ),
      },
      {
        accessorKey: "attribute49",
        header: "Mã đơn hàng kênh bán",
        size: 200,
        Cell: ({ renderedCellValue }) => {
          return <>{renderedCellValue}</>;
        },
      },
      {
        accessorKey: "attribute82",
        header: "Mã vận đơn",
        size: 200,
        Cell: ({ renderedCellValue }) => {
          return <>{renderedCellValue}</>;
        },
      },
      {
        accessorKey: "orderStatus",
        header: "Trạng thái đơn hàng",
        enableColumnFilter: false,
        size: 200,
        Cell: ({ row }) => {
          const orderStatus = row.original.orderStatus;
          let badgeColor;
          switch (orderStatus) {
            case "DON_HANG_TAM":
              badgeColor = "cyan";
              break;
            case "DON_HANG_CHOT":
              badgeColor = "violet";
              break;
            case "DON_HANG_DOI_TRA_TAM":
              badgeColor = "cyan";
              break;
            case "DON_HANG_HOAN_THANH":
              badgeColor = "green";
              break;
            case "DON_HANG_DOI_TRA":
              badgeColor = "green";
              break;
            case "THANH_TOAN_CHUA_HOAN_THANH":
              badgeColor = "red";
              break;
            case "pending":
              badgeColor = "yellow";
              break;
            case "DON_HANG_BAN_TAI_SHOP":
              badgeColor = "green";
              break;
            case "DON_HANG_CHO_DUYET_GIAM":
              badgeColor = olive_green.light;
              break;
            case "DON_HANG_DA_DUYET_GIAM":
              badgeColor = olive_green.medium;
              break;
            case "KHO_XUAT_HANG":
              badgeColor = sky_blue.medium;
              break;
            case "DON_HANG_DONG":
              badgeColor = "red";
              break;
            default:
              badgeColor = "gray";
          }
          return (
            <Badge
              size="md"
              color={badgeColor}
              fs={"12,5px"}
              fw={"bold"}
              radius="sm"
            >
              {row.original.orderStatus !== null
                ? getTextStatus(row)
                : "Không HỢP LỆ"}
            </Badge>
          );
        },
      },
      {
        accessorKey: "attribute58",
        header: "Trạng thái đơn hàng TMĐT",
        size: 240,
        Cell: ({ renderedCellValue }) => (
          <Text size="sm" fw={500} tt={"uppercase"}>
            {getDataAtt58(renderedCellValue?.toString() ?? "")}
          </Text>
        ),
      },
      {
        accessorKey: "trangThaiDoiSoat",
        header: "Trạng thái đối soát",
        Cell: ({ row }) =>
          String(row.original.saleChannelId) === "341" ? (
            row.original.attributeValue7 && row.original.attributeValue7 > 0 ? (
              <Badge color="green">Đã đối soát</Badge>
            ) : (
              <Badge color="orange">Chưa đối soát</Badge>
            )
          ) : (
            <></>
          ),
      },
      {
        accessorKey: "attribute12",
        header: "Trạng thái chung",
        Cell: ({ renderedCellValue }) => (
          <Text fw={500} size="12.5px">
            {getValueById(
              renderedCellValue?.toString() ?? "",
              dataStatusGeneral,
              "label",
            )?.toUpperCase() || renderedCellValue}
          </Text>
        ),
      },
      {
        accessorKey: "attribute53",
        header: "Trạng thái phân công",
        enableColumnFilter: false,
        size: 200,
        Cell: ({ renderedCellValue }) => (
          <Text size="sm" fw={500} tt={"uppercase"}>
            {renderedCellValue === "CHUA_PHAN_CONG"
              ? "Chưa phân công"
              : renderedCellValue === "DA_PHAN_CONG"
                ? "Đã phân công"
                : renderedCellValue === "DA_TIEP_NHAN"
                  ? "Đã tiếp nhận"
                  : ""}
          </Text>
        ),
      },
      {
        accessorKey: "attribute3",
        header: "Trạng thái kho",
        Cell: ({ row }) => {
          const _status = warehouseStatus.find(
            (status) => status.value === row?.original?.attribute3?.toString(),
          );

          return (
            <Badge size="sm" color="cyan">
              {_status?.label ? _status.label : "Chưa yêu cầu"}
            </Badge>
          );
        },
      },
      {
        accessorKey: "attribute37",
        header: "Ngày xuất kho",
        Cell: ({ renderedCellValue }: any) => {
          if (
            typeof renderedCellValue === "string" ||
            renderedCellValue instanceof Date
          ) {
            return <>{formatDateTransfer(renderedCellValue)}</>;
          }
          return <>{formatDateTransfer(undefined)}</>;
        },
      },
      {
        accessorKey: "orderType",
        header: "Loại đơn hàng",
        size: 150,
        enableColumnFilter: false,
        Cell: ({ row }) => {
          const orderType = orderTypes.find(
            (type) => type.value === row.original.orderType?.toString(),
          );

          return (
            <Badge color={orderType?.color || "gray"}>
              {orderType?.label || "KHÔNG HỢP LỆ"}
            </Badge>
          );
        },
      },
      {
        accessorKey: "attribute16",
        header: "Mã ĐTCN",
        size: 150,
        Cell: ({ renderedCellValue }) => {
          return <>{renderedCellValue}</>;
        },
      },
      {
        accessorKey: "saleChannelId",
        header: "Kênh bán",
        Cell: ({ renderedCellValue }) => {
          const saleChannel = dataChannel.find(
            (channel) => channel.value === renderedCellValue?.toString(),
          );
          return saleChannel?.text || "KHÔNG HỢP LỆ";
        },
      },

      {
        accessorKey: "invOrgName",
        header: "Kho hàng",
        enableColumnFilter: false,
        Cell: ({ row }) => (
          <>{`[${row.original.invOrgCode}] ${row.original.invOrgName}`}</>
        ),
      },
      {
        accessorKey: "sale",
        header: "Nhân viên bán hàng",
        size: 180,
        enableColumnFilter: false,
      },
      {
        accessorKey: "nvhs",
        header: "Người hưởng số",
        Cell: ({ renderedCellValue }) => {
          return <>{renderedCellValue}</>;
        },
      },
      {
        accessorKey: "salePhone",
        header: "Số đt người bán",
        Cell: ({ renderedCellValue }) => {
          return <>{renderedCellValue}</>;
        },
      },

      {
        accessorKey: "creator",
        header: "Người tạo đơn đổi trả",
        size: 200,
        enableColumnFilter: false,
        Cell: ({ row }) => {
          // Kiểm tra nếu orderType === 3 thì hiển thị, ngược lại không hiển thị
          return row.original.orderType == 3 ? <>{row.original.creator}</> : "";
        },
      },
      {
        accessorKey: "creationDate",
        header: "Ngày lập",
        size: 160,
        enableColumnFilter: false,
        Cell: ({ renderedCellValue }: any) => {
          if (
            typeof renderedCellValue === "string" ||
            renderedCellValue instanceof Date
          ) {
            return <>{formatDateTransfer(renderedCellValue)}</>;
          }
          return <>{formatDateTransfer(undefined)}</>;
        },
      },
      {
        accessorKey: "orderDate",
        header: "Ngày hoàn thành",
        enableColumnFilter: false,
        Cell: ({ renderedCellValue }: any) => {
          if (
            typeof renderedCellValue === "string" ||
            renderedCellValue instanceof Date
          ) {
            return <>{formatDateTransfer(renderedCellValue)}</>;
          }
          return <>{formatDateTransfer(undefined)}</>;
        },
      },
      {
        accessorKey: "technicalChecked",
        header: "Dịch vụ kiểm tra",
        enableColumnFilter: false,
        size: 200,
        Cell: ({ row }) => (
          <>
            {
              <>
                {(() => {
                  if (row.original.technicalChecked === 0) {
                    return <Badge color="indigo">Không yêu cầu</Badge>;
                  } else if (row.original.technicalChecked === 1) {
                    return <Badge color="grape">Lắp ráp(mới)</Badge>;
                  } else if (row.original.technicalChecked === 2) {
                    return <Badge color="cyan">Kiểm tra(mới)</Badge>;
                  } else if (row.original.technicalChecked === 3) {
                    return (
                      <Badge color={sky_blue.medium}>
                        Lắp ráp(mới)+Kiểm tra(mới)
                      </Badge>
                    );
                  } else {
                    return null; // Or any fallback UI if needed
                  }
                })()}
              </>
            }
          </>
        ),
      },
      {
        accessorKey: "attribute11",
        header: "KT đã kiểm tra",
        size: 260,
        Cell: ({ row }) => (
          <Badge color="blue">
            {row?.original?.attribute11
              ? row?.original?.attribute11
              : "Khách không yêu cầu kiểm tra"}
          </Badge>
        ),
      },
      {
        accessorKey: "deliveryStatus",
        header: "Trạng thái giao vận",
        Cell: ({ renderedCellValue }) => (
          <Text size="sm" fw={500} tt={"uppercase"}>
            {getDataDeliveryStatus(renderedCellValue?.toString() ?? null)}
          </Text>
        ),
      },
      {
        accessorKey: "shipingVendor",
        header: "Bộ phận giao hàng",
        enableColumnFilter: false,
        Cell: ({ row }) => (
          <>{getShippingVendorText(row?.original?.shipingVendor ?? "")}</>
        ),
      },
      {
        accessorKey: "tenNVGV",
        header: "Tên NV giao vận",
        Cell: ({ renderedCellValue }) => {
          return <>{renderedCellValue}</>;
        },
      },
      {
        accessorKey: "sdtNVGV",
        header: "SĐT NV giao vận",
        Cell: ({ renderedCellValue }) => {
          return <>{renderedCellValue}</>;
        },
      },
      {
        accessorKey: "attribute46",
        header: "Thời gian GV tiếp nhận đơn",
        enableColumnFilter: false,
        size: 225,
        Cell: ({ renderedCellValue }: any) => {
          if (
            typeof renderedCellValue === "string" ||
            renderedCellValue instanceof Date
          ) {
            return <>{formatDateTransfer(renderedCellValue)}</>;
          }
          return <>{formatDateTransfer(undefined)}</>;
        },
      },
      {
        accessorKey: "attribute45",
        header: "Thời gian GV hoàn thành đơn",
        enableColumnFilter: false,
        size: 240,
        Cell: ({ renderedCellValue }: any) => {
          if (
            typeof renderedCellValue === "string" ||
            renderedCellValue instanceof Date
          ) {
            return <>{formatDateTransfer(renderedCellValue)}</>;
          }
          return <>{formatDateTransfer(undefined)}</>;
        },
      },

      {
        accessorKey: "customerName",
        header: "Tên khách hàng",
        enableColumnFilter: false,
      },
      {
        accessorKey: "customerPhone",
        header: "Điện thoại KH",
        size: 160,
        enableColumnFilter: false,
      },
      {
        accessorKey: "totalAmount",
        header: "Tổng tiền hàng",
        size: 160,
        enableColumnFilter: false,
        Cell: ({ row }) => {
          let listAdd = row.original.orderDetail || [];
          let priceTotal = 0;
          listAdd?.forEach((item: any) => {
            priceTotal += (item?.priceOnPriceList || 0) * (item?.quality || 0);
          });
          return (
            <NumberFormatter
              value={priceTotal}
              thousandSeparator="."
              decimalSeparator=","
            />
          );
        },
      },
      {
        accessorKey: "totalPromotion",
        header: "Giảm giá",
        size: 160,
        enableColumnFilter: false,
        Cell: ({ row }) => {
          let listAdd = row.original.orderDetail || [];
          const totalDiscount = listAdd.reduce((acc: number, item: any) => {
            const discountAmount = item.discountAmount || 0;
            return acc + discountAmount * (item.quality || 1);
          }, 0);
          const totalDiscountRate = listAdd.reduce((acc: number, item: any) => {
            const discountRate = item.discountRate || 0;
            const price = item.priceOnPriceList || 0;
            const quality = item.quality || 1;
            const discountValue = (discountRate / 100) * price * quality;
            return acc + discountValue;
          }, 0);
          const totalPromotionOrder = totalDiscount + totalDiscountRate;
          return (
            <NumberFormatter
              value={totalPromotionOrder}
              thousandSeparator="."
              decimalSeparator=","
            />
          );
        },
      },
      {
        accessorKey: "amountAfterDiscount",
        header: "Sau giảm giá",
        size: 160,
        enableColumnFilter: false,
        Cell: ({ row }) => {
          let listAdd = row.original.orderDetail || [];
          let priceTotal = 0;
          listAdd?.forEach((item: any) => {
            priceTotal += (item?.priceOnPriceList || 0) * (item?.quality || 0);
          });
          const totalDiscount = listAdd.reduce((acc: number, item: any) => {
            const discountAmount = item.discountAmount || 0;
            return acc + discountAmount * (item.quality || 1);
          }, 0);
          const totalDiscountRate = listAdd.reduce((acc: number, item: any) => {
            const discountRate = item.discountRate || 0;
            const price = item.priceOnPriceList || 0;
            const quality = item.quality || 1;
            const discountValue = (discountRate / 100) * price * quality;
            return acc + discountValue;
          }, 0);
          const totalPromotionOrder = totalDiscount + totalDiscountRate;
          const displayTotalAmount = priceTotal;
          const amountAfterDiscount = Math.max(
            displayTotalAmount - totalPromotionOrder,
            0,
          );
          return (
            <NumberFormatter
              value={amountAfterDiscount}
              thousandSeparator="."
              decimalSeparator=","
            />
          );
        },
      },
      {
        accessorKey: "amountDiscountTotal",
        header: "Tổng phí sàn",
        size: 160,
        enableColumnFilter: false,
        Cell: ({ row }) => {
          let listAdd = row.original.orderDetail || [];
          let sumAttr28 = listAdd.reduce(
            (sum: number, item: any) => sum + (Number(item.attribute28) || 0),
            0,
          );
          const totalFloorFee =
            Number(row.original.amountDiscountTotal ?? 0) || sumAttr28 || 0;
          return (
            <NumberFormatter
              value={totalFloorFee === 0 ? 0 : `-${Math.abs(totalFloorFee)}`}
              thousandSeparator="."
              decimalSeparator=","
            />
          );
        },
      },
      {
        accessorKey: "attributeValue7",
        header: "Phí sàn cập nhật sau",
        size: 160,
        enableColumnFilter: false,
        Cell: ({ row }) => {
          let floorFeeAfter = Number(
            row.original.attributevalue7 ??
              row.original.attributeValue7 ??
              row.original.attribute17 ??
              0,
          );
          if (isNaN(floorFeeAfter)) floorFeeAfter = 0;
          return (
            <NumberFormatter
              value={floorFeeAfter === 0 ? 0 : `-${Math.abs(floorFeeAfter)}`}
              thousandSeparator="."
              decimalSeparator=","
            />
          );
        },
      },
      {
        accessorKey: "amount",
        header: "Số tiền cần thanh toán",
        size: 160,
        enableColumnFilter: false,
        Cell: ({ renderedCellValue }) => (
          <NumberFormatter
            value={(renderedCellValue as string | number) || 0}
            thousandSeparator="."
            decimalSeparator=","
          />
        ),
      },
      {
        accessorKey: "paid",
        header: "Đã trả",
        size: 160,
        enableColumnFilter: false,
        Cell: ({ renderedCellValue }) => (
          <NumberFormatter
            value={(renderedCellValue as string | number) || 0}
            thousandSeparator="."
            decimalSeparator=","
          />
        ),
      },
      {
        accessorKey: "remaining",
        header: "Còn nợ",
        size: 160,
        enableColumnFilter: false,
        Cell: ({ renderedCellValue }) => (
          <NumberFormatter
            value={(renderedCellValue as string | number) || 0}
            thousandSeparator=","
          />
        ),
      },
      {
        accessorKey: "attribute39",
        header: "Mã voucher",
        Cell: ({ renderedCellValue }) => {
          return <>{renderedCellValue}</>;
        },
      },
      {
        accessorKey: "attribute27",
        header: "Số hóa đơn",
        Cell: ({ renderedCellValue }) => {
          return <>{renderedCellValue}</>;
        },
      },
      {
        accessorKey: "printInvoiceCheck",
        header: "Xuất hóa đơn",
        enableColumnFilter: false,
        Cell: ({ row }) => <>{row.original.printInvoiceCheck}</>,
      },
      {
        accessorKey: "attribute60",
        header: "Lý do hủy đơn",
      },
      {
        accessorKey: "action",
        header: "Thao tác",
        enableSorting: false,
        enableResizing: false,
        enableColumnActions: false,
        enableColumnFilter: false,
        size: 90,
        Cell: ({ row }) => (
          <Tooltip label="Danh sách sản phẩm trong đơn">
            <ActionIcon
              variant="light"
              aria-label="Danh sách sản phẩm trong đơn"
              color="blue"
              onClick={() =>
                openModal(
                  row.original.orderDetail,
                  row.original.amount,
                  row.original.orderNumber,
                )
              }
            >
              <IconEye size={20} stroke={1.5} />
            </ActionIcon>
          </Tooltip>
        ),
      },
    ],
    [],
  );

  const table = useMantineReactTable({
    columns,
    data,
    enableColumnResizing: true,
    positionToolbarAlertBanner: "bottom",
    renderTopToolbarCustomActions: () => <></>,
    mantineTopToolbarProps: {
      style: {
        display: "none",
      },
    },
    localization: _localization,
    enableRowSelection: false,
    getRowId: (row) => row.id?.toString(),
    initialState: {
      showColumnFilters: false,
      columnPinning: {
        left: ["STT", "orderNumber"],
        right: ["action"],
      },
      columnVisibility: { id: false },
      density: "xs",
    },
    mantineTableContainerProps: {
      style: { maxHeight: TABLE_HEIGHT, minHeight: TABLE_HEIGHT },
    },
    enableStickyHeader: true,
    enableExpanding: false,
    manualFiltering: false,
    manualPagination: true,
    manualSorting: false,
    rowCount,
    onColumnFiltersChange: setColumnFilters,
    onGlobalFilterChange: setGlobalFilter,
    onPaginationChange: setPagination,
    onSortingChange: setSorting,
    mantineTableBodyCellProps: () => ({
      style: styleTableMantine,
    }),
    state: {
      columnFilters,
      globalFilter,
      isLoading,
      pagination,
      showSkeletons: isLoading,
      sorting,
      rowSelection,
    },
    mantinePaginationProps: {
      showRowsPerPage: true,
      withEdges: true,
      rowsPerPageOptions: ["20", "50", "100"],
    },
    paginationDisplayMode: "pages",
    enableColumnPinning: true,
    mantineTableProps: {
      striped: false,
    },
  });

  // Hàm gọi API chi nhánh
  const fetchDataBranch = async () => {
    const dataApi = await repositoryPos.get<MessageResponse<any>>(
      "/api/v1/TblDmInventory/get-select-branch",
    );

    if (dataApi && dataApi.success) {
      const filteredData = dataApi.data.slice(1);
      setDataBranch(filteredData);
    }
  };

  const ExportExcel = async () => {
    const search = form.getValues();
    const queryParams = {
      Skip: pagination.pageIndex * pagination.pageSize,
      Take: pagination.pageSize,
      InvId: search.invId ? search.invId.toString() : "",
      InvOutwardId: search.invOutwardId ? search.invOutwardId.toString() : "",
      CustomerId: search.customerId ? search.customerId.toString() : "",
      StartDate: search.StartDate
        ? moment(search?.StartDate).format("YYYY-MM-DDT00:00:00.000[Z]")
        : "",
      EndDate: search.EndDate
        ? moment(search?.EndDate).format("YYYY-MM-DDT00:00:00.000[Z]")
        : "",
      OrderStatus: search.orderStatus,
      OrderType: search.orderType ? search.orderType : 0,
      OrderNumber: search.orderNumber,
      PaymentStatus: search.paymentStatus,
      industry: search.industry,
      OutwardStatus: search?.OutwardStatus,
      KeySearch: search.keySearch,
      saleChannelId: search.saleChannelId
        ? search.saleChannelId.toString()
        : "",
      AttributeValue7: search.AttributeValue7,
      Attribute58: search.Attribute58, // Trạng thái đơn hàng TMĐT
      Attribute12: search.Attribute12, // Trạng thái chung
    };

    const url =
      "/api/v1/CreateSellItem/export-sale-order-tmdt?IsOnline=false&IsInstallment=false&IsAccountant=false&IsEcommerce=true&" +
      Object.entries(queryParams)
        .filter(([, value]) => value !== undefined && value !== "")
        .map(([key, value]) => `${key}=${encodeURIComponent(value)}`)
        .join("&");

    const _url = BASE_API_POS + url;

    const fullUrl = new URL(_url);

    const token = localStorage.getItem("token");
    open();
    try {
      const response = await fetch(fullUrl.toString(), {
        method: "GET", // Use the appropriate HTTP method
        headers: {
          Authorization: `Bearer ${token?.replace(/"/g, "")}`,
          "Content-Type": "application/vnd.ms-excel",
        },
      });

      if (!response.ok) {
        throw new Error("Failed to fetch the resource");
      }

      const blob = await response.blob();
      const blobUrl = window.URL.createObjectURL(blob);
      const _name = new Date().toISOString().slice(0, 10);
      const filename = `bao_cao_ban_hang-${_name}.xlsx`; // Tên mặc định nếu không có header
      const link = document.createElement("a");
      link.href = blobUrl;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(blobUrl);
    } catch (error) {
      console.error("Error:", error);
    } finally {
      close();
    }
  };

  useEffect(() => {
    fetchData();
  }, [pagination.pageIndex, pagination.pageSize, sorting]);

  useEffect(() => {
    SocketExtension.On(CREATEORDERSUCCESS, () => {
      setIsHaveNewOrder(true);
    });
    return () => {
      SocketExtension.Off(CREATEORDERSUCCESS, () => {});
    };
  }, []);

  const renderSelectOption: SelectProps["renderOption"] = ({
    option,
    checked,
  }) => (
    <Group flex="1" gap="xs">
      <Badge
        color={orderTypes.find((x) => x.value === option.value)?.color}
        radius="sm"
        variant="filled"
      ></Badge>
      {option.label}{" "}
      {checked && <IconCheck style={{ marginInlineStart: "auto" }} />}
    </Group>
  );

  useEffect(() => {
    startTransition(() => {
      fetchDataBranch();
    });
  }, []);

  useEffect(() => {
    if (!checkedPinSearch) {
      form.setValues((prev) => ({ ...prev, keySearch: "" }));
    }
  }, [checkedPinSearch]);
  useHotkeys([["Enter", () => runSearch()]]);

  useEffect(() => {
    const fetchEmployeeBy = async () => {
      const response = await repositoryPos.get<MessageResponse<any>>(
        `/api/v1/TblDmEmployee/get-all`,
      );
      if (response && response.success) {
        const result = response.data;
        const formattedData = result.map((employee: any) => ({
          value: employee.code, // Giá trị cho "value"
          label: `${employee.fullname}-${employee.code}`, // Giá trị cho "label"
        }));
        // Lọc trùng theo "value": mỗi value chỉ giữ lại 1 object đầu tiên
        const uniqueData = Array.from(
          new Map(
            formattedData.map((item: any) => [item.value, item]),
          ).values(),
        );
        setListEmployeeSales(uniqueData); // Lưu mảng đã format (đã lọc trùng) vào state
      }
    };
    fetchEmployeeBy();
  }, []);

  return (
    <Box pos={"relative"} ref={rootRef}>
      <LoadingOverlayCustom visible={visible} />
      <div ref={headerRef}>
        <Box p={10} style={{ border: "1px solid var(--mantine-color-gray-3)" }}>
          <Flex
            gap="md"
            justify={"space-between"}
            align="center"
            direction="row"
            wrap="wrap"
            w={"100%"}
          >
            <_breadcrumb></_breadcrumb>
            <div>
              <Button
                leftSection={<IconReload size={14} />}
                variant="blue"
                disabled={!isHaveNewOrder}
                mr={"xs"}
                onClick={() => {
                  fetchData();
                  setIsHaveNewOrder(false);
                }}
              >
                Cập nhật đơn mới
              </Button>
              <Button
                leftSection={<IconDownload size={14} />}
                color="violet"
                variant="outline"
                onClick={async () => ExportExcel()}
              >
                Export Excel
              </Button>
            </div>
          </Flex>

          <FilterSection
            activeCount={activeFilters.length}
            isLoading={isLoading}
            onSearch={runSearch}
            onClear={handleClearFilters}
            quickFilters={
              <>
                <TextInput
                  key={form.key("keySearch")}
                  aria-label="Từ khoá"
                  placeholder="Nhập từ khoá tìm kiếm"
                  leftSection={
                    <IconSearch size={16} color="var(--mantine-color-cyan-6)" />
                  }
                  style={{ flex: 1, minWidth: 240 }}
                  {...form.getInputProps("keySearch")}
                  onKeyDown={onEnterSearch}
                />
                <DatePickerInput
                  type="range"
                  aria-label="Khoảng ngày"
                  placeholder="Từ ngày → Đến ngày"
                  leftSection={
                    <IconCalendarEvent
                      size={16}
                      color="var(--mantine-color-cyan-6)"
                    />
                  }
                  locale="vi"
                  valueFormat="DD/MM/YYYY"
                  labelSeparator="→"
                  clearable
                  w={{ base: "100%", sm: 250 }}
                  defaultValue={[
                    innitStateSearch.StartDate,
                    innitStateSearch.EndDate,
                  ]}
                  onChange={handleDateRangeChange}
                />
              </>
            }
            chips={
              // Các bộ lọc mở rộng đang áp dụng, bấm x để bỏ từng bộ lọc
              activeFilters.length > 0 && (
                <Group gap={6} mt="xs">
                  <Text size="xs" c="dimmed">
                    Đang lọc:
                  </Text>
                  {activeFilters.map((field) => (
                    <Pill
                      key={field.key}
                      withRemoveButton
                      onRemove={() => handleRemoveFilter(field)}
                      removeButtonProps={{
                        "aria-label": `Bỏ lọc ${field.label}`,
                      }}
                    >
                      {field.label}:{" "}
                      <b>{getFilterText(field, appliedFilters[field.key])}</b>
                    </Pill>
                  ))}
                  <Button
                    variant="subtle"
                    color="gray"
                    size="compact-xs"
                    leftSection={<IconFilterOff size={14} />}
                    onClick={handleClearFilters}
                  >
                    Xoá bộ lọc
                  </Button>
                </Group>
              )
            }
            advancedFilters={
              <SimpleGrid
                cols={{ base: 1, xl: 2 }}
                spacing="xl"
                verticalSpacing="md"
              >
                <FilterGroup title="Đơn hàng">
                  <TextInput
                    key={form.key("orderNumber")}
                    label="Mã đơn hàng"
                    placeholder="Nhập mã đơn hàng"
                    {...form.getInputProps("orderNumber")}
                    onKeyDown={onEnterSearch}
                  />
                  <TextInput
                    key={form.key("VourcherCode")}
                    label="Mã voucher"
                    placeholder="Nhập mã voucher"
                    {...form.getInputProps("VourcherCode")}
                    onKeyDown={onEnterSearch}
                  />
                  <Select
                    key={form.key("orderType")}
                    label="Loại đơn hàng"
                    placeholder="Tất cả"
                    data={orderTypes}
                    renderOption={renderSelectOption}
                    nothingFoundMessage="Dữ liệu không có !"
                    searchable
                    clearable
                    {...form.getInputProps("orderType")}
                  />
                  <Select
                    key={form.key("saleChannelId")}
                    label="Kênh bán"
                    placeholder="Tất cả"
                    data={saleChannelOptions}
                    searchable
                    clearable
                    {...form.getInputProps("saleChannelId")}
                  />
                </FilterGroup>

                <FilterGroup title="Trạng thái">
                  <Select
                    key={form.key("orderStatus")}
                    label="Trạng thái đơn hàng"
                    placeholder="Tất cả"
                    data={orderStatuses}
                    clearable
                    {...form.getInputProps("orderStatus")}
                  />
                  <Select
                    key={form.key("Attribute58")}
                    label="Trạng thái đơn TMĐT"
                    placeholder="Tất cả"
                    data={tmdtStatusOptions}
                    searchable
                    clearable
                    {...form.getInputProps("Attribute58")}
                  />
                  <Select
                    key={form.key("Attribute12")}
                    label="Trạng thái chung"
                    placeholder="Tất cả"
                    data={dataStatusGeneral}
                    searchable
                    clearable
                    {...form.getInputProps("Attribute12")}
                  />
                  <Select
                    key={form.key("AttributeValue7")}
                    label="Trạng thái đối soát"
                    placeholder="Tất cả"
                    data={reconcileOptions}
                    clearable
                    {...form.getInputProps("AttributeValue7")}
                  />
                </FilterGroup>

                <FilterGroup title="Khách hàng & người bán">
                  <TextInput
                    key={form.key("codeCustomer")}
                    label="Mã khách hàng"
                    placeholder="Nhập mã khách hàng"
                    {...form.getInputProps("codeCustomer")}
                    onKeyDown={onEnterSearch}
                  />
                  <TextInput
                    key={form.key("phone")}
                    label="SĐT khách hàng / người mua"
                    placeholder="Nhập số điện thoại"
                    {...form.getInputProps("phone")}
                    onKeyDown={onEnterSearch}
                  />
                  <TextInput
                    key={form.key("SalePhone")}
                    label="SĐT người bán"
                    placeholder="Nhập số điện thoại"
                    {...form.getInputProps("SalePhone")}
                    onKeyDown={onEnterSearch}
                  />
                  <Select
                    key={`${form.key("CodeSal")}-${listEmployeeSales.length}`}
                    label="Người hưởng số"
                    placeholder="Tất cả"
                    data={listEmployeeSales}
                    searchable
                    clearable
                    {...form.getInputProps("CodeSal")}
                  />
                </FilterGroup>

                <FilterGroup title="Xử lý đơn">
                  <Select
                    key={`${form.key("invId")}-${dataBranch.length}`}
                    label="Chi nhánh"
                    placeholder="Tất cả"
                    data={branchOptions}
                    searchable
                    clearable
                    {...form.getInputProps("invId")}
                  />
                  <Select
                    key={form.key("IsAssigned")}
                    label="Tình trạng phân công"
                    placeholder="Tất cả"
                    data={assignOptions}
                    clearable
                    {...form.getInputProps("IsAssigned")}
                  />
                  <DatePickerInput
                    key={form.key("FinishDate")}
                    label="Ngày hoàn thành đơn"
                    placeholder="Chọn ngày"
                    locale="vi"
                    valueFormat="DD/MM/YYYY"
                    clearable
                    {...form.getInputProps("FinishDate")}
                  />
                  <DatePickerInput
                    key={form.key("OwDate")}
                    label="Ngày xuất kho"
                    placeholder="Chọn ngày"
                    locale="vi"
                    valueFormat="DD/MM/YYYY"
                    clearable
                    {...form.getInputProps("OwDate")}
                  />
                </FilterGroup>
              </SimpleGrid>
            }
          />
        </Box>
      </div>
      <div style={{ marginTop: "10px" }}>
        <MantineReactTable table={table} />
      </div>
    </Box>
  );
};

export default ListOrderTMDTReadOnly;
